import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Download,
  FilePenLine,
  FileSearch,
  FlaskConical,
  Search,
  SendHorizontal,
  UserPlus,
  UserRoundPlus,
  Wrench,
} from 'lucide-react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { RequestMvrDialog } from '@/components/Mvr/RequestMvrDialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { UserLabel } from '@/components/ui/user-label'
import { ADMIN_BASE_URL, adminHref, isProduction } from '@/config/config'
import { CacheKeys } from '@/lib/cache'
import { customerTabHref } from '@/lib/customerTabs'
import { useFeatureFlag } from '@/models/flag'
import { MVR_FEATURE_FLAG } from '@/models/mvr'
import {
  exportOrg,
  isOrgShopping,
  type Org,
  staffUserName,
  updateOrgSalesOwner,
  useCreateLeadFromCustomer,
  useStaffUsers,
} from '@/models/user'
import { AddOrgMemberDialog } from '../AdminSettings/AddOrgMemberDialog'
import { AddTestPolicyDialog } from './AddTestPolicyDialog'
import { ConfirmLinkDialog } from './ConfirmLinkDialog'
import { CUSTOMER_POLICY_CHANGES_TAB } from './CustomerNav'
import { CustomerSidebarAction } from './CustomerSidebarAction'
import { RequoteButton } from './RequoteButton'

interface CustomerActionSidebarProps {
  org: Org
}

/** The customer page's sales owner picker and per-customer actions, under the nav in the left rail. */
export function CustomerActionSidebar({ org }: CustomerActionSidebarProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { staffUsers } = useStaffUsers()
  const createLeadMutation = useCreateLeadFromCustomer(org.id)
  const mvrEnabled = useFeatureFlag(MVR_FEATURE_FLAG)
  // Once carriers are asked the customer's page is their quotes, which have links of their own.
  const confirmLinkOpen = isOrgShopping(org) && !org.currentInsuranceApplication?.hasDispatchedIndications

  const salesOwnerMutation = useMutation({
    mutationFn: (salesOwnerId: string | null) => updateOrgSalesOwner(org.id, salesOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, org.id] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    },
  })

  const exportMutation = useMutation({
    mutationFn: () => exportOrg(org.id),
    onError: () => toast.error('Failed to export customer'),
  })

  function handleCreateLead() {
    createLeadMutation.mutate(undefined, {
      onSuccess: (result) => {
        toast.success('Sales lead created')
        void navigate(`/insure/internal/sales-leads/${result.leadId}`)
      },
      onError: (err: unknown) => {
        const leadId = (err as { response?: { data?: { leadId?: string } } }).response?.data?.leadId
        if (leadId) {
          void navigate(`/insure/internal/sales-leads/${leadId}`)
        } else {
          toast.error('Failed to create sales lead')
        }
      },
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">Sales owner</Label>
        <Select
          value={org.salesOwner?.id ?? 'unassigned'}
          onValueChange={(value) => salesOwnerMutation.mutate(value === 'unassigned' ? null : value)}
          disabled={salesOwnerMutation.isPending}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {[...staffUsers]
              .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
              .map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  <UserLabel name={staffUserName(u)} src={u.avatar} />
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <Separator className="my-1" />

      {org.sourceLeadId ? (
        <CustomerSidebarAction
          variant="outline"
          icon={<UserPlus />}
          label="View Lead"
          href={`/insure/internal/sales-leads/${org.sourceLeadId}`}
        />
      ) : (
        <CustomerSidebarAction
          icon={<UserPlus />}
          label="Create Lead"
          onClick={handleCreateLead}
          loading={createLeadMutation.isPending ? 'Creating…' : undefined}
        />
      )}

      <CustomerSidebarAction
        variant="outline"
        icon={<FilePenLine />}
        label="Start a policy change"
        disabled={!(org.currentPolicies ?? []).length}
        hint={(org.currentPolicies ?? []).length ? undefined : 'No policy in force to change.'}
        onClick={() => void navigate(customerTabHref(org.id, CUSTOMER_POLICY_CHANGES_TAB, 'new'))}
      />
      <CustomerSidebarAction
        variant="outline"
        icon={<Search />}
        label="CAB Lookup"
        href={org.business?.dotNumber ? `/insure/internal/cab/${org.business.dotNumber}` : undefined}
        target="_blank"
        disabled={!org.business?.dotNumber}
        hint={org.business?.dotNumber ? undefined : 'Motor carrier must have a DOT number for CAB lookup'}
      />

      <RequoteButton applicationId={org.currentInsuranceApplication?.id} />

      {confirmLinkOpen && (
        <CustomerSidebarAction
          variant="outline"
          icon={<SendHorizontal />}
          label="Confirm link"
          hint="Email the customer a link to check, confirm and send their application"
          wrap={(trigger) => <ConfirmLinkDialog orgId={org.id}>{trigger}</ConfirmLinkDialog>}
        />
      )}

      {mvrEnabled && (
        <CustomerSidebarAction
          variant="outline"
          icon={<FileSearch />}
          label="Request MVR"
          wrap={(trigger) => <RequestMvrDialog orgId={org.id}>{trigger}</RequestMvrDialog>}
        />
      )}

      <CustomerSidebarAction
        variant="outline"
        icon={<UserRoundPlus />}
        label="Add user"
        wrap={(trigger) => (
          <AddOrgMemberDialog orgId={org.id} orgName={org.name}>
            {trigger}
          </AddOrgMemberDialog>
        )}
      />

      {!isProduction && (
        <CustomerSidebarAction
          variant="outline"
          icon={<FlaskConical />}
          label="Add test policy"
          wrap={(trigger) => (
            <AddTestPolicyDialog orgId={org.id} orgName={org.name}>
              {trigger}
            </AddTestPolicyDialog>
          )}
        />
      )}

      <CustomerSidebarAction
        variant="outline"
        icon={<Download />}
        label="Export"
        hint="Download this customer's data (no documents) to import from the QA page locally"
        onClick={() => exportMutation.mutate()}
        loading={exportMutation.isPending ? 'Exporting…' : undefined}
      />

      <CustomerSidebarAction
        variant="outline"
        icon={<Wrench />}
        label="Django admin"
        hint="Open this customer in Django admin"
        href={adminHref(`${ADMIN_BASE_URL}/insure/org/${org.id}/change/`)}
        target="_blank"
      />
    </div>
  )
}
